import { CustomValidationService } from './modules/validation/validation-service';
import { NativeValidationService } from './modules/validation/native-validation-service';
import { LoadingOverlayService } from './modules/ui/loading-overlay-service';
import { AddressService } from './modules/address/address-service';
import { CheckoutAddressService } from './modules/address/checkout-address-service';
import { PaymentService } from './modules/payment/payment-service';
import { FormService } from './modules/forms/form-service';
import { UIUtilities } from './modules/ui/ui-utilities';
import { initSwipers } from './modules/ui/swiper'
import { initLucideIcons } from './modules/ui/lucide-icons'
import { initDataNumbers } from './modules/ui/data-number'
import { initChoices } from './modules/ui/choices'
import { initInPageSubnav } from './modules/ui/in-page-subnav'
import { initBookingPage } from './modules/events/event-booking-service';
import { initCheckoutPage, initStudyMaterialsHelpText } from './modules/events/event-checkout-service';
import { initCourseBookingPage } from './modules/courses/course-booking-service';
import { initCourseCheckoutPage } from './modules/courses/course-checkout-service';
import { initRecaptchaFormGuard } from './modules/forms/recaptcha-form-guard';

// Import styles and dependencies
import './scss/main.scss'
import * as bootstrap from 'bootstrap'
import Swiper from 'swiper'
import { Navigation, Pagination } from 'swiper/modules'

console.log("Starting Frontend...");

// Application class that coordinates all modules
class App {
    private validationService: CustomValidationService;
    private addressService: AddressService;
    private checkoutAddressService: CheckoutAddressService;
    private paymentService: PaymentService;
    private formService: FormService;

    constructor() {
        this.validationService = new CustomValidationService();
        this.addressService = new AddressService();
        this.checkoutAddressService = new CheckoutAddressService();
        this.paymentService = new PaymentService();
        this.formService = new FormService();
    }

    public initialize(): void {
        // Initialize validation first
        this.validationService.initialize();
        
        // Initialize other services when DOM is ready
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => {
                this.initializeServices();
            });
        } else {
            // DOM is already ready
            this.initializeServices();
        }

        function initCarousel(root: Element) {
            const track = root.querySelector(".mega-menu__track");
            if (!track) return;

            const slides = track.querySelectorAll<HTMLElement>(".mega-menu__slide");
            const indicators = root.querySelectorAll(".mega-menu__indicator");

            root.querySelectorAll(".mega-menu__arrow").forEach((btn: any) => {
                btn.addEventListener("click", () => {
                    const dir = btn.dataset.dir === "next" ? 1 : -1;
                    track.scrollBy({ left: slides[0].offsetWidth * dir, behavior: "smooth" });
                });
            });

            indicators.forEach((btn: any) => {
                btn.addEventListener("click", () => {
                    const index = parseInt(btn.dataset.index, 10);
                    slides[index].scrollIntoView({
                        behavior: "smooth",
                        block: "nearest",
                        inline: "start",
                    });
                });
            });

            track.addEventListener("scroll", () => {
                const activeIndex = Math.round(track.scrollLeft / slides[0].offsetWidth);
                indicators.forEach((ind: any, i: any) => {
                    ind.classList.toggle("active", i === activeIndex);
                });
            });
        }

        const panels = document.getElementById("mobileNavPanels");
        const exploreBtn = document.getElementById("exploreCoursesBtn");
        const backBtn = document.getElementById("mobileNavBack");

        if (panels && exploreBtn && backBtn) {
            let carouselInitialized = false;

            exploreBtn.addEventListener("click", () => {
                panels.classList.add("mobile-nav__panels--drilled");
                exploreBtn.setAttribute("aria-expanded", "true");

                if (!carouselInitialized) {
                    const handler = (e: TransitionEvent) => {
                        if (e.target !== panels.querySelector(".mobile-nav__panel--explore"))
                            return;
                        const carousel = panels.querySelector(
                            ".mobile-nav__panel--explore .mega-menu-carousel",
                        );
                        if (carousel) initCarousel(carousel);
                        carouselInitialized = true;
                        panels.removeEventListener("transitionend", handler);
                    };
                    panels.addEventListener("transitionend", handler);
                }
            });

            backBtn.addEventListener("click", () => {
                panels.classList.remove("mobile-nav__panels--drilled");
                exploreBtn.setAttribute("aria-expanded", "false");
            });
        }

        // Info modal
        const infoModal = document.getElementById("infoModal");
        if (infoModal) {
            infoModal.addEventListener("show.bs.modal", (e) => {
                const btn = (e as any).relatedTarget;
                if (!btn) return;

                const card = btn.closest(".partner-card");
                const title = btn.dataset.partnerTitle || "";
                const logo = card?.querySelector(".partner-card__logo")?.src || "";
                const url = btn.dataset.partnerUrl || "#";

                (infoModal.querySelector("#infoModalTitle") as HTMLElement).innerHTML =
                    `Gold Partner:<br>${title}`;
                const img = infoModal.querySelector("#infoModalLogo") as HTMLImageElement;
                img.src = logo;
                img.alt = title;
                (infoModal.querySelector("#infoModalLink") as HTMLAnchorElement).href = url;

                const tmpl = btn
                    .closest(".partner-card")
                    ?.querySelector(".partner-card__desc");
                const body = infoModal.querySelector("#infoModalBody")!;
                body.innerHTML = tmpl ? tmpl.innerHTML : "";
            });
        }

        // Course info modal
        const courseInfoModal = document.getElementById("courseInfoModal");
        if (courseInfoModal) {
            courseInfoModal.addEventListener("show.bs.modal", (e) => {
                const btn = (e as any).relatedTarget;
                if (!btn) return;

                const tmpl = btn.closest(".bg-grey-1")?.querySelector(".course-tile__desc");
                const body = courseInfoModal.querySelector("#courseInfoModalBody")!;
                body.innerHTML = tmpl ? tmpl.innerHTML : "";
            });
        }

        // Custom select dropdowns
        document.querySelectorAll(".custom-select").forEach((select) => {
            const toggle = select.querySelector(".form-select");
            select.querySelectorAll(".dropdown-item").forEach((item) => {
                item.addEventListener("click", () => {
                    toggle!.textContent = item.textContent?.trim() ?? "";
                });
            });
        });

        // Filter toggle buttons
        document.querySelectorAll(".btn-filter[aria-pressed]").forEach((btn) => {
            btn.addEventListener("click", () => {
                const isPressed = btn.getAttribute("aria-pressed") === "true";
                btn.setAttribute("aria-pressed", String(!isPressed));
                btn.classList.toggle("active", !isPressed);
            });
        });

        // Backdrop overlay
        document
            .querySelectorAll(".filter-dropdown, .sub-nav-dropdown, .mega-menu-dropdown")
            .forEach((dropdown) => {
                let backdrop: HTMLDivElement | null = null;

                dropdown.addEventListener("shown.bs.dropdown", () => {
                    backdrop = document.createElement("div");
                    backdrop.className = "dropdown-backdrop";
                    document.body.appendChild(backdrop);
                    requestAnimationFrame(() => backdrop!.classList.add("show"));
                });

                dropdown.addEventListener("hidden.bs.dropdown", () => {
                    if (!backdrop) return;
                    backdrop.classList.remove("show");
                    const el = backdrop;
                    backdrop = null;
                    setTimeout(() => el.remove(), 150);
                });
            });

        // Back to top buttons
        document.querySelectorAll<HTMLButtonElement>("button.back-to-top").forEach((btn) => {
            btn.addEventListener("click", () => {
                window.scrollTo({ top: 0, behavior: "smooth" });
            });
        });

        // Filter dropdown: close on header click
        document.querySelectorAll(".filter-dropdown__header").forEach((header) => {
            header.addEventListener("click", () => {
                const dropdown = header.closest(".filter-dropdown");
                const toggle = dropdown?.querySelector('[data-bs-toggle="dropdown"]');
                bootstrap.Dropdown.getOrCreateInstance(toggle!).hide();
            });
        });

        // Filter dropdown: close on Escape key. Uses capture phase at document level so
        // the event is intercepted regardless of where focus is (Bootstrap doesn't move
        // focus into the menu when there are no .dropdown-item elements, only checkboxes).
        document.addEventListener("keydown", (e) => {
            if (e.key !== "Escape") return;
            const openMenu = document.querySelector<HTMLElement>(".filter-dropdown .dropdown-menu.show");
            if (!openMenu) return;
            const dropdownEl = openMenu.closest<HTMLElement>(".filter-dropdown");
            const toggle = dropdownEl?.querySelector<HTMLElement>('[data-bs-toggle="dropdown"]');
            if (toggle) bootstrap.Dropdown.getOrCreateInstance(toggle).hide();
        }, true);

        // Video facade: thumbnail → iframe on click
        document.querySelectorAll<HTMLElement>("[data-video-src]").forEach((wrapper) => {
            const playBtn = wrapper.querySelector(".play-btn");
            if (!playBtn) return;

            playBtn.addEventListener("click", () => {
                const ratioBox = wrapper.querySelector(".ratio");
                const iframe = document.createElement("iframe");
                iframe.src = wrapper.dataset.videoSrc + "?autoplay=1";
                iframe.allow =
                    "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture";
                iframe.allowFullscreen = true;
                ratioBox!.innerHTML = "";
                ratioBox!.appendChild(iframe);
                playBtn.remove();
            });
        });

        // Card sliders
        document.querySelectorAll<HTMLElement>(".card-slider").forEach((el) => {
            const slideCount = el.querySelectorAll(".swiper-slide").length;
            new Swiper(el, {
                modules: [Navigation, Pagination],
                loop: slideCount >= 3,
                slidesPerView: 1.4,
                spaceBetween: 16,
                pagination: {
                    el: el.querySelector(".swiper-pagination") as HTMLElement,
                    clickable: true,
                },
                navigation: {
                    nextEl: el.querySelector(".swiper-button-next") as HTMLElement,
                    prevEl: el.querySelector(".swiper-button-prev") as HTMLElement,
                },
                breakpoints: {
                    992: {
                        slidesPerView: 3,
                        spaceBetween: 32,
                    },
                },
            });
        });
    }

    private initializeServices(): void {
        this.addressService.initialize();
        this.formService.initialize();
        initRecaptchaFormGuard();
        
        // Set up global functions for backward compatibility
        this.setupGlobalFunctions();

        // Initialise UI widgets
        initSwipers();
        initLucideIcons();
        initDataNumbers();
        initChoices();
        initInPageSubnav();

        // Only initialize checkout address service on checkout pages
        if (document.getElementById('EventBookingForm') || document.getElementById('CourseBookingForm')) {
            this.checkoutAddressService.initialize();
        }

        // Initialise Booking Page if applicable
        if (document.getElementById('EventBookingPageForm')) {
            initBookingPage();
        }

        // Initialise Course Booking Page if applicable
        if (document.getElementById('CourseBookingPageForm')) {
            initCourseBookingPage();
        }

        // Initialise Checkout Page if applicable
        if (document.getElementById('EventBookingForm')) {
            initStudyMaterialsHelpText();

            if (document.getElementById('paymentMethodTabs')) {
                initCheckoutPage(this.paymentService);
            }
        }

        // Initialise Course Checkout Page if applicable
        if (document.getElementById('CourseBookingForm') && document.getElementById('paymentMethodTabs')) {
            initCourseCheckoutPage(this.paymentService);
        }
    }

    private setupGlobalFunctions(): void {
        // Make payment functions available globally for Stripe integration
        window.initializeEventBookingPayment = this.paymentService.initializeEventBookingPayment.bind(this.paymentService);
        window.initializeCourseBookingPayment = this.paymentService.initializeCourseBookingPayment.bind(this.paymentService);
        window.initializeMembershipApplicationPayment = this.paymentService.initializeMembershipApplicationPayment.bind(this.paymentService);

        
        // Make App object available globally for backward compatibility
        window.App = {
            defaults: {
                pageName: "Pagination.CurrentPage"
            },
            
            init: () => this.initialize(),
            
            initForms: () => this.formService.initialize(),
            
            smoothScrollTo: UIUtilities.smoothScrollTo,
            
            slideUp: UIUtilities.slideUp,
            
            slideDown: UIUtilities.slideDown,
            
            initSwitchList: UIUtilities.initSwitchList,
            
            // Modern services
            loadingOverlay: LoadingOverlayService.getInstance(),
            nativeValidation: NativeValidationService.getInstance(),
            
            // Legacy methods for backward compatibility
            performAjax: (element: HTMLFormElement, successFunc: Function, errorFunc: Function) => {
                // Delegate to form service's performAjax method
                (this.formService as any).performAjax(element, successFunc, errorFunc);
            },
            
            resetFormPage: (element: HTMLFormElement) => {
                (this.formService as any).resetFormPage(element);
            },
            
            changePage: (element: Element, pageNum?: number) => {
                (this.formService as any).changePage(element, pageNum);
            }
        };
    }
}



// Initialize the application
const app = new App();
app.initialize();

// Export for module usage if needed
//export default app;